import React, { useEffect, useMemo } from 'react';
import { useSubscription } from '@apollo/client';
import { IconArrowLink, IconEco } from '../../../../../../assets/icons';
import { useInstallationContext } from '../../../../../../context/installation';
import {
  ChannelTypeInternal,
  MeasurementChannelKind,
  MeasurementSubscriptionResponse,
  OnDashboardMeasurementSubscription,
  OnDashboardMeasurementSubscriptionVariables,
  ScalarParameterTypeInternal,
  SubPriority,
  VectorParameterTypeInternal,
} from '../../../../../../data-access/gql-types/graphql';
import { ON_DASHBOARD_MEASUREMENT } from '../../../../../../data-access/subscriptions/on-measurement';
import { useMeasurementMapOperations } from '../../../../hooks/measurement-map/use-measurement-map-operations';
import { measurementParameters } from '../../../../modules/channel-details/measurements/utils';
import { useChannelsStateStore } from '../../../../store/channels-state';
import { ChannelMeterStateType, ChannelMeterType, ChannelType } from '../../../../types';
import './index.scss';

type MeterControlProps = {
  channel: ChannelType;
};

export const MeterControl: React.FC<MeterControlProps> = ({ channel }) => {
  const { measurementMap, convertMeasurementToNumber } = useMeasurementMapOperations();
  const setChannelState = useChannelsStateStore((s) => s.setChannelState);
  const { skipLavvaFetch } = useInstallationContext();
  const supportedScalars = (channel.data as ChannelMeterType).supportedScalarParameters;

  const ecoIndicatorNumberType = convertMeasurementToNumber(ChannelTypeInternal.Meter)(
    'scalars',
    ScalarParameterTypeInternal.EcoIndicator,
  ) as number;
  const ecoIndicatorSupported = supportedScalars.includes(ecoIndicatorNumberType);

  const { data } = useSubscription<OnDashboardMeasurementSubscription, OnDashboardMeasurementSubscriptionVariables>(
    ON_DASHBOARD_MEASUREMENT,
    {
      variables: {
        input: {
          channelId: channel.id,
          deviceId: channel.deviceId,
          kind: MeasurementChannelKind.Meter,
          priority: SubPriority.High,
          scalarParameterTypes: ecoIndicatorSupported ? [ecoIndicatorNumberType] : [],
          vectorParameterTypes: [
            convertMeasurementToNumber(MeasurementChannelKind.Meter)(
              'vectors',
              VectorParameterTypeInternal.ActivePower,
            ),
          ],
          indices: [],
        },
      },
      skip: !measurementMap || skipLavvaFetch,
    },
  );

  useEffect(() => {
    const measurements =
      (data?.onDashboardMeasurement.data as MeasurementSubscriptionResponse)?.vectorMeasurements || [];

    const ecoRatio = measurements.find((x) => x.type === ecoIndicatorNumberType)?.value;
    if (ecoRatio != null) setChannelState(channel.id, { ecoRatio } as ChannelMeterStateType);
  }, [data?.onDashboardMeasurement]);

  const value = useMemo(() => {
    if (data) {
      if ('vectorMeasurements' in data.onDashboardMeasurement.data) {
        const sum = data.onDashboardMeasurement.data.vectorMeasurements.sum((x) => x.value);
        if (sum !== undefined) return sum;
      }
    }

    return null;
  }, [data]);

  const params = measurementParameters[VectorParameterTypeInternal.ActivePower];

  const isGreaterUnit = useMemo(() => {
    return value != null ? Math.abs(value) > 1000 : value;
  }, [value]);

  if (value === null) return null;

  return (
    <div className="meter-control">
      {value !== undefined ? (
        <div className="meter-control__value-wrapper">
          {value < 0 ? <IconEco /> : null}
          <span className="meter-control__value">{isGreaterUnit ? (value / 1000).toFixed(1) : value.toFixed(1)}</span>
          <span className="meter-control__unit">{isGreaterUnit ? params.unit2 : params.unit}</span>
          {value ? <IconArrowLink size={22} direction={value < 0 ? 'up' : 'down'} /> : null}
        </div>
      ) : null}
    </div>
  );
};
